import { useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { KeyRound, Check, X } from "lucide-react";

/**
 * v109.9.35 — Password policy mirrored client-side. Rules match backend
 * `password_meets_policy` (server.py:174):
 *   - >= 8 characters
 *   - at least one uppercase + one lowercase
 *   - at least one digit
 *   - at least one non-alphanumeric special character
 * Extra client-only rule: `confirm` matches `new` so users don't
 * accidentally lock themselves out.
 */
const PASSWORD_RULES = [
  { key: "len",     label: "At least 8 characters",           test: (p) => (p || "").length >= 8 },
  { key: "upper",   label: "One uppercase letter (A-Z)",      test: (p) => /[A-Z]/.test(p || "") },
  { key: "lower",   label: "One lowercase letter (a-z)",      test: (p) => /[a-z]/.test(p || "") },
  { key: "digit",   label: "One digit (0-9)",                 test: (p) => /\d/.test(p || "") },
  { key: "special", label: "One special character (!@#$…)",   test: (p) => /[^a-zA-Z0-9]/.test(p || "") },
];

function passwordMeetsPolicy(pwd) {
  return PASSWORD_RULES.every((r) => r.test(pwd));
}

/**
 * ProfileQuickActions
 * v109.9.34 — Photo upload removed per user request. Only the Reset-Password
 * action remains, now rendered as a labelled Button (was an icon-only circle).
 */
export default function ProfileQuickActions() {
  const { user } = useAuth();
  const [pwdOpen, setPwdOpen] = useState(false);

  if (!user) return null;

  return (
    <div className="flex items-center gap-2" data-testid="profile-quick-actions">
      <Button
        type="button"
        onClick={() => setPwdOpen(true)}
        variant="outline"
        className="h-10 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:border-amber-400 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/40 font-semibold"
        data-testid="dash-change-password-btn"
      >
        <KeyRound size={15} strokeWidth={2.2} className="mr-1.5" /> Reset Password
      </Button>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const { refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setCur(""); setNext(""); setConfirm(""); };

  const policyOk = passwordMeetsPolicy(next);
  const matchOk = next.length > 0 && next === confirm;
  const canSubmit = cur.length > 0 && policyOk && matchOk && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!policyOk) return toast.error("Password does not meet the requirements");
    if (!matchOk) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      reset();
      refresh();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="dash-pwd-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Reset Password</DialogTitle>
          <DialogDescription>Use a strong, unique password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="dash-change-password-form">
          <div>
            <Label className="text-[12px] uppercase tracking-wider font-semibold text-slate-600">Current Password</Label>
            <Input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5 h-10" data-testid="dash-pwd-current" />
          </div>
          <div>
            <Label className="text-[12px] uppercase tracking-wider font-semibold text-slate-600">New Password</Label>
            <Input type="password" required value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5 h-10" data-testid="dash-pwd-new" />
            {/* Live policy checklist — turns green as each rule is met */}
            <ul className="mt-2 space-y-1 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2" data-testid="dash-pwd-policy">
              {PASSWORD_RULES.map((rule) => {
                const ok = rule.test(next);
                return (
                  <li
                    key={rule.key}
                    className={`flex items-center gap-2 text-[12px] transition-colors ${
                      ok ? "text-emerald-700 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"
                    }`}
                    data-testid={`dash-pwd-rule-${rule.key}`}
                    data-ok={ok ? "yes" : "no"}
                  >
                    <span
                      className={`w-4 h-4 rounded-full grid place-items-center shrink-0 ${
                        ok ? "bg-emerald-100 dark:bg-emerald-500/20" : "bg-slate-200 dark:bg-slate-700"
                      }`}
                    >
                      {ok ? <Check size={11} strokeWidth={3} /> : <X size={10} strokeWidth={2.5} />}
                    </span>
                    <span className={ok ? "font-semibold" : ""}>{rule.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <Label className="text-[12px] uppercase tracking-wider font-semibold text-slate-600">Confirm New Password</Label>
            <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-10" data-testid="dash-pwd-confirm" />
            {/* Match indicator — only shown once user starts typing confirm */}
            {confirm.length > 0 && (
              <div
                className={`mt-1.5 text-[11.5px] flex items-center gap-1.5 ${
                  matchOk ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"
                }`}
                data-testid="dash-pwd-match"
                data-ok={matchOk ? "yes" : "no"}
              >
                {matchOk ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={2.5} />}
                {matchOk ? "Passwords match" : "Passwords don't match yet"}
              </div>
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!canSubmit} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] text-white" data-testid="dash-pwd-submit">
              {loading ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
